import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Button, Skeleton, Typography, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IconSearchOff,
  IconAdjustmentsHorizontal,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import LandingLayout from '../../components/Layouts/LandingLayout';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../contextProviders/AuthProvider';
import HomeSearchBar, { HomeSearchState } from '../../components/HomeSearchBar';
import FiltersModal from '../../components/FiltersModal';
import SpacesMap, { IMapBounds, SpacesMapHandle } from '../../components/SpacesMap';
import MapCategoryPicker from '../../components/MapCategoryPicker';
import { useMapCategoryViews } from '../../hooks/useMapCategoryViews';
import ExploreMobileMap from './ExploreMobileMap';
import { SheetSnapPoint } from '../../components/SnapBottomSheet';
import ListPagination from '../../components/ListPagination';
import GearLoadingState from '../../components/GearLoadingState';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { searchHeavyDutyMachineries } from '../../services/HeavyDutyMachineryService';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IHeavyDutyMachineryFacets, IHeavyDutyMachinerySearchFilters } from '../../interfaces/heavyDutyMachinery/ISearch';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import HeavyDutyMachineryCard from './HeavyDutyMachineryCard';

const { Text } = Typography;

const DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 20;

const MAP_PANEL_WIDTH = '42%';
const MAP_PANEL_MIN_WIDTH = 320;
const MAP_PANEL_GAP = 20;
const MAP_SLIDE_MS = 320;
const MAP_SLIDE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
// What the list's width resolves to next to the map's own MAP_PANEL_WIDTH --
// mirrors what `flex: 1` would already compute on its own, but as an
// explicit value the list panel can transition *from* when the map goes
// fullscreen (a `flex` basis has nothing to animate from/to on its own).
// The `max()` keeps it in step with the map panel's own MAP_PANEL_MIN_WIDTH
// floor on narrower `lg` viewports.
const LIST_PANEL_WIDTH = `calc(100% - max(${MAP_PANEL_WIDTH}, ${MAP_PANEL_MIN_WIDTH}px) - ${MAP_PANEL_GAP}px)`;

const SKELETON_CARD_COUNT = 6;

/** Same grid shape (and column widths) as the real results, so swapping
 * skeleton -> real cards doesn't jump the layout -- just the content fades. */
const ResultsSkeleton: React.FC = () => (
  <Row gutter={[20, 28]} style={{ opacity: 0.5 }}>
    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
      <Col xs={24} md={12} xxl={8} key={i}>
        <Skeleton.Image active style={{ width: '100%', aspectRatio: '16 / 10', borderRadius: 16 }} />
        <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 1, width: '40%' }} style={{ marginTop: 10 }} />
      </Col>
    ))}
  </Row>
);

const parseCsvParam = (params: URLSearchParams, key: string): string[] | undefined => {
  const raw = params.get(key);
  return raw ? raw.split(',').filter(Boolean) : undefined;
};

const filtersFromSearchParams = (params: URLSearchParams): IHeavyDutyMachinerySearchFilters => ({
  city: params.get('city') || undefined,
  lat: params.get('lat') ? Number(params.get('lat')) : undefined,
  lng: params.get('lng') ? Number(params.get('lng')) : undefined,
  radiusKm: params.get('radiusKm') ? Number(params.get('radiusKm')) : undefined,
  rentalMode: params.get('rentalMode') || undefined,
  startDate: params.get('startDate') || undefined,
  endDate: params.get('endDate') || undefined,
  availabilityMode: (params.get('availabilityMode') as 'Now' | 'At' | null) || undefined,
  availabilityAt: params.get('availabilityAt') || undefined,
  minPrice: params.get('minPrice') ? Number(params.get('minPrice')) : undefined,
  maxPrice: params.get('maxPrice') ? Number(params.get('maxPrice')) : undefined,
  category: parseCsvParam(params, 'category'),
  accessType: parseCsvParam(params, 'accessType'),
  brand: parseCsvParam(params, 'brand'),
  verifiedBadge: params.get('verifiedBadge') === 'true' || undefined,
  weightTonsMin: params.get('weightTonsMin') ? Number(params.get('weightTonsMin')) : undefined,
  weightTonsMax: params.get('weightTonsMax') ? Number(params.get('weightTonsMax')) : undefined,
  powerHpMin: params.get('powerHpMin') ? Number(params.get('powerHpMin')) : undefined,
  powerHpMax: params.get('powerHpMax') ? Number(params.get('powerHpMax')) : undefined,
  dryHireOnly: params.get('dryHireOnly') === 'true' || undefined,
  wetHireOnly: params.get('wetHireOnly') === 'true' || undefined,
  deliveryOnly: params.get('deliveryOnly') === 'true' || undefined,
  page: params.get('page') ? Number(params.get('page')) : 1,
  pageSize: params.get('pageSize') ? Number(params.get('pageSize')) : DEFAULT_PAGE_SIZE,
});

const filtersToSearchParams = (filters: IHeavyDutyMachinerySearchFilters): URLSearchParams => {
  const params = new URLSearchParams();
  const setIfPresent = (key: string, value: string | number | undefined | null) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  };
  setIfPresent('city', filters.city);
  setIfPresent('lat', filters.lat);
  setIfPresent('lng', filters.lng);
  setIfPresent('radiusKm', filters.radiusKm);
  setIfPresent('rentalMode', filters.rentalMode);
  setIfPresent('startDate', filters.startDate);
  setIfPresent('endDate', filters.endDate);
  setIfPresent('availabilityMode', filters.availabilityMode);
  setIfPresent('availabilityAt', filters.availabilityAt);
  setIfPresent('minPrice', filters.minPrice);
  setIfPresent('maxPrice', filters.maxPrice);
  setIfPresent('category', filters.category?.join(','));
  setIfPresent('accessType', filters.accessType?.join(','));
  setIfPresent('brand', filters.brand?.join(','));
  if (filters.verifiedBadge) params.set('verifiedBadge', 'true');
  setIfPresent('weightTonsMin', filters.weightTonsMin);
  setIfPresent('weightTonsMax', filters.weightTonsMax);
  setIfPresent('powerHpMin', filters.powerHpMin);
  setIfPresent('powerHpMax', filters.powerHpMax);
  if (filters.dryHireOnly) params.set('dryHireOnly', 'true');
  if (filters.wetHireOnly) params.set('wetHireOnly', 'true');
  if (filters.deliveryOnly) params.set('deliveryOnly', 'true');
  if (filters.page && filters.page > 1) params.set('page', String(filters.page));
  if (filters.pageSize && filters.pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(filters.pageSize));
  return params;
};

const HeavyDutyMachineries: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<IHeavyDutyMachinerySearchFilters>(() => filtersFromSearchParams(searchParams));
  const [items, setItems] = useState<IHeavyDutyMachinery[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [facets, setFacets] = useState<IHeavyDutyMachineryFacets | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  // Bumped every time the search bar actually resolves a point (see
  // handleSearchBarSubmit) -- SpacesMap's fit-to-results effect uses this,
  // not filters.lat/lng directly, to know "the guest just asked to be taken
  // somewhere": searching the exact same place twice in a row geocodes to
  // identical coordinates, which a plain lat/lng comparison there mistook
  // for "nothing changed" and silently skipped the second refit.
  const [locationSearchSeq, setLocationSearchSeq] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hoveredSpaceId, setHoveredSpaceId] = useState<string | undefined>(undefined);
  // Which side set hoveredSpaceId -- only a map-originated hover should
  // auto-scroll the list (see the effect below); hovering a card directly
  // means the user is already looking right at it.
  const [hoverSource, setHoverSource] = useState<'map' | 'card' | undefined>(undefined);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Below lg the map would squeeze the list into an unusable column, so the
  // list takes the full width instead and the map is dropped entirely.
  const { isDesktop, isMobile } = useAppBreakpoint();
  const showMap = isDesktop;

  // Mobile's own map+sheet view (ExploreMobileMap) needs this lifted up here
  // rather than kept as its own local state -- collapsing the sheet also
  // hides AppLayout's MobileBottomNav entirely (see hideBottomNav below),
  // which only AppLayout itself can control.
  const [mobileSnapPoint, setMobileSnapPoint] = useState<SheetSnapPoint>('media');

  // Collapsing slides the map off to the right and gives the list the whole
  // width. The map stays mounted while hidden -- remounting it would mean a
  // fresh token call and a spinner on every toggle.
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const mapVisible = showMap && !mapCollapsed;
  const mapPanelRef = useRef<HTMLDivElement>(null);
  const [mapPanelWidth, setMapPanelWidth] = useState<number | undefined>(undefined);
  const lastMapBoundsRef = useRef<IMapBounds | undefined>(undefined);
  // Whichever of desktop's <SpacesMap> / mobile's <ExploreMobileMap> is
  // actually mounted (the two branches below are mutually exclusive) --
  // lets handleSearchBarSubmit read the map's *current* viewport on demand
  // (see its own comment) instead of only finding out about it after the
  // next moveend.
  const mapApiRef = useRef<SpacesMapHandle>(null);

  // Expands the map to the panel's full width and hides the list instead --
  // the opposite of mapCollapsed (which hides the map and gives the list
  // the full width). Doesn't make sense combined with either mapCollapsed or
  // a hidden map, so the effect below drops it the moment either happens.
  const [mapFullscreen, setMapFullscreen] = useState(false);
  useEffect(() => {
    if (!mapVisible) setMapFullscreen(false);
  }, [mapVisible]);

  const debouncedFilters = useDebouncedValue(filters, DEBOUNCE_MS);

  useEffect(() => {
    setSearchParams(filtersToSearchParams(debouncedFilters), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    searchHeavyDutyMachineries(debouncedFilters)
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotalCount(result.totalCount ?? result.items.length);
        setFacets(result.facets);
      })
      .catch(() => {
        if (cancelled) return;
        // Don't leave the previous (now stale, no-longer-matching-the-filters)
        // results on screen when a search fails -- that reads as "the filter
        // did nothing" instead of "the request errored".
        setItems([]);
        setTotalCount(0);
        setFacets(undefined);
        message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_LOAD_ERROR));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const updateFilters = (patch: Partial<IHeavyDutyMachinerySearchFilters>, resetPage = true) => {
    // Marks a search in flight the instant the filter itself changes, in the
    // same batch as setFilters below -- not DEBOUNCE_MS later, when the
    // debounced-filters effect (the only other place that touches `loading`)
    // finally runs. SpacesMap's own fit-to-results effect leans on this: it
    // gates a new location search's refit on `!resultsLoading` specifically
    // so it never fits the *previous* search's (still on-screen) results to
    // the *new* lat/lng. Without this, the interval between the filter
    // change and the debounced effect catching up was exactly such a
    // still-`loading=false`, stale-results window -- long enough to "spend"
    // that refit on the wrong data and leave the map's camera wherever it
    // was, since a spent refit never fires again once the real results land.
    setLoading(true);
    setFilters((prev) => ({ ...prev, ...patch, page: resetPage ? 1 : patch.page ?? prev.page }));
  };

  const handleSearchBarSubmit = (values: HomeSearchState) => {
    const isHourly = values.rentalMode === 'Hourly';
    const hasLocation = values.city != null || values.lat != null;
    // Nothing typed into "Where" reads, to the guest, as "search wherever
    // I'm currently looking" -- same as any other map-based search product
    // -- not "search literally everywhere". Anchoring to the map's own
    // current viewport (read live, not from the last reported moveend --
    // that can be stale right after a location search, whose own camera
    // move is deliberately not reported as a bounds change) gets that
    // without waiting for the guest to nudge the map themselves first.
    // Desktop already has its own explicit "Search this area" button for
    // this same gesture, so this only applies on mobile.
    const liveBounds = !hasLocation && isMobile ? mapApiRef.current?.getBounds() : undefined;
    updateFilters({
      city: values.city,
      lat: values.lat,
      lng: values.lng,
      // Same field FiltersModal's own Radius slider reads/writes -- set
      // whenever the search bar resolves an actual point (near me, or a
      // geocoded suggestion -- see HomeSearchBar's LOCATION_DEFAULT_RADIUS_KM),
      // left unset otherwise.
      radiusKm: values.radiusKm,
      // A fresh location search is meant to be fully described by
      // city/lat/lng/radiusKm above -- any minLat/maxLat/minLng/maxLng left
      // over from panning the map before this submit would otherwise go on
      // silently double-narrowing it (see handleBoundsChange's own comment
      // on why point+radius and viewport bounds are never meant to both
      // apply at once). liveBounds (above) is the one exception: a
      // location-less submit anchoring to the current viewport instead.
      minLat: liveBounds?.minLat,
      maxLat: liveBounds?.maxLat,
      minLng: liveBounds?.minLng,
      maxLng: liveBounds?.maxLng,
      rentalMode: values.rentalMode,
      startDate: isHourly ? undefined : values.dateRange?.[0].format('YYYY-MM-DD'),
      endDate: isHourly ? undefined : values.dateRange?.[1].format('YYYY-MM-DD'),
      availabilityMode: isHourly ? values.availabilityMode : undefined,
      availabilityAt: isHourly && values.availabilityMode === 'At' ? values.availabilityAt?.toISOString() : undefined,
    });
    if (values.lat != null && values.lng != null) setLocationSearchSeq((seq) => seq + 1);
  };

  // On mobile this fires on every debounced pan/zoom (see ExploreMobileMap's
  // own comment); on desktop only when the user clicks SpacesMap's "Search
  // this area" button. Either way, viewport bounds and a point+radius
  // location search (city/lat/lng/radiusKm) are two competing ways of
  // saying "what area to search" -- ApplyFilters ANDs whichever are present
  // together server-side, so with both set the guest could pan/zoom the map
  // without ever touching the actual location filter and still trigger a
  // real refetch that (at best) repeats the exact same result the radius
  // search already settled on, or (at worst) silently clips it further to
  // whatever happens to be on screen. Skipped entirely while a point+radius
  // search is active -- that's the authoritative "what area" here, and the
  // map is just a view onto its results until the guest changes the
  // location or radius themselves.
  const handleBoundsChange = useCallback((bounds: IMapBounds) => {
    // Remembered so collapsing and reopening can put the same viewport back
    // (see the effect below) instead of losing where the user had panned to.
    lastMapBoundsRef.current = bounds;
    setFilters((prev) => {
      if (prev.lat != null && prev.lng != null && prev.radiusKm != null) return prev;
      if (
        prev.minLat === bounds.minLat &&
        prev.maxLat === bounds.maxLat &&
        prev.minLng === bounds.minLng &&
        prev.maxLng === bounds.maxLng
      ) {
        // Same box (e.g. a click that didn't actually move the camera) --
        // returning prev keeps the reference stable so no refetch fires.
        return prev;
      }
      return { ...prev, ...bounds, page: 1 };
    });
  }, []);

  // Hiding the map -- whether by dropping below lg or by collapsing it -- has
  // to drop its bbox with it, otherwise the list stays silently narrowed to a
  // viewport nobody can see. Bringing it back re-applies the last viewport it
  // reported, so a collapse/expand round trip changes nothing.
  useEffect(() => {
    setFilters((prev) => {
      if (!mapVisible) {
        if (prev.minLat == null && prev.maxLat == null && prev.minLng == null && prev.maxLng == null) return prev;
        return { ...prev, minLat: undefined, maxLat: undefined, minLng: undefined, maxLng: undefined, page: 1 };
      }
      // Same reasoning as handleBoundsChange above -- don't reintroduce a
      // viewport box while a point+radius location search is the
      // authoritative "what area" filter.
      if (prev.lat != null && prev.lng != null && prev.radiusKm != null) return prev;
      // Nothing to restore on the first render -- the map reports its own
      // bounds as soon as it settles on a camera.
      const bounds = lastMapBoundsRef.current;
      if (!bounds) return prev;
      if (
        prev.minLat === bounds.minLat &&
        prev.maxLat === bounds.maxLat &&
        prev.minLng === bounds.minLng &&
        prev.maxLng === bounds.maxLng
      ) {
        return prev;
      }
      return { ...prev, ...bounds, page: 1 };
    });
  }, [mapVisible]);

  // The map holds its own width while the panel around it animates shut, so
  // it slides out to the side rather than being squeezed flat -- which means
  // that width has to be a pixel value, remeasured whenever the panel is
  // genuinely open and laid out. Zero-ish readings are ignored: they're the
  // panel mid-animation, not a new size.
  const measureMapPanel = useCallback(() => {
    const width = mapPanelRef.current?.getBoundingClientRect().width;
    if (width && width > 1) setMapPanelWidth(width);
  }, []);

  useEffect(() => {
    if (!mapVisible) return;
    measureMapPanel();
    window.addEventListener('resize', measureMapPanel);
    return () => window.removeEventListener('resize', measureMapPanel);
  }, [mapVisible, measureMapPanel]);

  // Hovering a pin scrolls its card into view if the list has scrolled it
  // out of frame -- only for a map-originated hover (hovering a card
  // directly means it's already right where the user is looking).
  useEffect(() => {
    if (hoverSource !== 'map' || !hoveredSpaceId) return;
    cardRefs.current[hoveredSpaceId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [hoveredSpaceId, hoverSource]);

  // Same "anchor to the map's current viewport instead of searching
  // literally everywhere" reasoning as handleSearchBarSubmit's own
  // liveBounds above (mobile only -- desktop has its own explicit "Search
  // this area" button for this gesture) -- FiltersModal's "Limpiar
  // filtros" is a second, separate way to end up with no location filter,
  // and clearing through it should behave identically to clearing through
  // the search bar's own quick "x", not fall back to an unconstrained
  // search just because it took a different path to get there.
  const clearFilters = () => {
    setLoading(true);
    const liveBounds = isMobile ? mapApiRef.current?.getBounds() : undefined;
    setFilters({
      page: 1,
      pageSize: filters.pageSize,
      minLat: liveBounds?.minLat,
      maxLat: liveBounds?.maxLat,
      minLng: liveBounds?.minLng,
      maxLng: liveBounds?.maxLng,
    });
  };

  const activeFilterCount =
    (filters.minPrice != null || filters.maxPrice != null ? 1 : 0) +
    (filters.radiusKm != null ? 1 : 0) +
    (filters.verifiedBadge ? 1 : 0) +
    (filters.weightTonsMin != null || filters.weightTonsMax != null ? 1 : 0) +
    (filters.powerHpMin != null || filters.powerHpMax != null ? 1 : 0) +
    (filters.dryHireOnly ? 1 : 0) +
    (filters.wetHireOnly ? 1 : 0) +
    (filters.deliveryOnly ? 1 : 0) +
    [
      filters.category,
      filters.accessType,
      filters.brand,
    ].filter((v) => v && v.length > 0).length;

  // Memoized (not a plain object literal) so its reference only actually
  // changes when one of these specific fields does -- HomeSearchBar re-syncs
  // its own displayed city/etc whenever this reference changes (see that
  // component's own comment), so a stable reference here is what keeps that
  // resync from firing -- and clobbering whatever the guest might be mid-
  // typing in the search bar -- on every unrelated re-render of this page.
  const searchBarInitialValues: HomeSearchState = useMemo(
    () => ({
      city: filters.city,
      lat: filters.lat,
      lng: filters.lng,
      radiusKm: filters.radiusKm,
      rentalMode: filters.rentalMode,
      dateRange:
        filters.rentalMode !== 'Hourly' && filters.startDate && filters.endDate
          ? [dayjs(filters.startDate), dayjs(filters.endDate)]
          : undefined,
      availabilityMode: filters.rentalMode === 'Hourly' ? filters.availabilityMode : undefined,
      availabilityAt:
        filters.rentalMode === 'Hourly' && filters.availabilityMode === 'At' && filters.availabilityAt
          ? dayjs(filters.availabilityAt)
          : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.city,
      filters.lat,
      filters.lng,
      filters.radiusKm,
      filters.rentalMode,
      filters.startDate,
      filters.endDate,
      filters.availabilityMode,
      filters.availabilityAt,
    ]
  );

  // Shared between both placements below (next to the search bar on
  // desktop/tablet, folded into the results-count row on mobile -- see
  // that row further down) so the button itself is only ever defined once.
  // Plain neutral pill either way -- no warm fill here (that stayed on the
  // search card's own summary control, see HomeSearchBar, but this button
  // no longer needs to visually match it now that the card itself is gone).
  const filtersButton = (
    <Button
      icon={<IconAdjustmentsHorizontal size={16} />}
      onClick={() => setFiltersOpen(true)}
      style={{ height: 'auto', padding: '15px 20px', borderRadius: 999 }}
    >
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
      {activeFilterCount > 0 && ` (${activeFilterCount})`}
    </Button>
  );

  // All four categories -- machinery, accessories, operators, services -- come
  // off one shared hook, which the mobile map also uses (see
  // useMapCategoryViews). Machinery is passed IN rather than owned there,
  // because its search is everything above: the URL, the facets, the
  // availability window. The other three own their own viewport-and-a-tab state
  // inside the hook and stay quiet until their tab is the active one.
  //
  // Desktop used to show machinery and nothing else, which meant three of the
  // four things this marketplace rents could not be browsed at all from a
  // laptop.
  const categories = useMapCategoryViews({
    machinery: {
      items,
      loading,
      totalCount,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
      onPageChange: (page) => updateFilters({ page }, false),
      onPageSizeChange: (pageSize) => updateFilters({ pageSize }),
      activeFilterCount,
      onOpenFilters: () => setFiltersOpen(true),
      searchBarInitialValues,
      onSearchBarSubmit: handleSearchBarSubmit,
      onBoundsChange: handleBoundsChange,
      onOpenSpace: (spaceId) => navigate(`/heavy-duty-machinery/${spaceId}`),
      locationSearchSeq,
    },
    hoveredItemId: hoveredSpaceId,
  });
  const { activeCategory, view } = categories;
  // Machinery keeps its own richer list rendering below (per-card refs for the
  // map's scroll-to-card, hover source tracking, staggered entrance). The other
  // three render the same card grid without any of that, so the machinery path
  // stays exactly the code it has always been rather than being generalised
  // into something that has to serve four shapes.
  const isMachinery = activeCategory === 'machinery';

  // Mobile (<768px) gets its own map+draggable-sheet view entirely (see
  // ExploreMobileMap) instead of the stacked-cards-only layout below --
  // tablet/desktop keep that layout exactly as it was (this only branches on
  // `isMobile`, never `isTablet`/`isDesktop`). Early return rather than
  // folding into `content` below: the two layouts share almost nothing
  // (fixed full-bleed map+sheet vs. a normal-flow column), so building them
  // as one shared JSX tree would mean more conditionals than the two
  // branches are actually alike.
  if (isMobile) {
    const mobileView = (
      <>
        <ExploreMobileMap
          ref={mapApiRef}
          items={items}
          totalCount={totalCount}
          loading={loading}
          locationSearchSeq={locationSearchSeq}
          page={filters.page ?? 1}
          pageSize={filters.pageSize ?? DEFAULT_PAGE_SIZE}
          onPageChange={(page) => updateFilters({ page }, false)}
          onPageSizeChange={(pageSize) => updateFilters({ pageSize })}
          searchBarInitialValues={searchBarInitialValues}
          onSearchBarSubmit={handleSearchBarSubmit}
          onBoundsChange={handleBoundsChange}
          activeFilterCount={activeFilterCount}
          onOpenFilters={() => setFiltersOpen(true)}
          onOpenSpace={(spaceId) => navigate(`/heavy-duty-machinery/${spaceId}`)}
          snapPoint={mobileSnapPoint}
          onSnapPointChange={setMobileSnapPoint}
        />
        <FiltersModal
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          filters={filters}
          facets={facets}
          totalCount={totalCount}
          onChange={updateFilters}
          onClear={clearFilters}
        />
      </>
    );

    if (authLoading) return null;
    if (!isAuthenticated) return <LandingLayout hideHeader>{mobileView}</LandingLayout>;
    return (
      <AppLayout hideFooter hideHeader hideBottomNav={mobileSnapPoint === 'collapsed'}>
        {mobileView}
      </AppLayout>
    );
  }

  const content = (
    <div style={{ width: '100%', maxWidth: 1300, textAlign: 'left', margin: isMobile ? '0 -12px' : undefined }}>
      {/* Which of the four this page is showing. Above the search bar rather
          than over the map (where mobile puts it, for lack of anywhere else):
          on desktop it is changing what the whole page is about, and the search
          bar right under it changes with it. */}
      <div style={{ marginBottom: 16 }}>
        <MapCategoryPicker active={activeCategory} onChange={categories.switchCategory} />
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <HomeSearchBar
            mode={view.searchBarMode}
            initialValues={view.searchBarInitialValues}
            onSearch={view.onSearchBarSubmit}
            style={{ margin: 0, maxWidth: 'none' }}
          />
        </div>
        {!isMobile && (
          <Button
            icon={<IconAdjustmentsHorizontal size={16} />}
            onClick={view.openFilters}
            style={{ height: 'auto', padding: '15px 20px', borderRadius: 999 }}
          >
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
            {view.filterCount > 0 && ` (${view.filterCount})`}
          </Button>
        )}
      </div>

      {/* List on the left, map on the right -- the map owns the viewport and
          narrows the list to what's on screen (see handleBoundsChange). */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div
          style={
            showMap
              ? {
                  width: mapFullscreen ? '0%' : mapCollapsed ? '100%' : LIST_PANEL_WIDTH,
                  minWidth: 0,
                  flexShrink: 0,
                  overflow: 'hidden',
                  opacity: mapFullscreen ? 0 : 1,
                  transition: `width ${MAP_SLIDE_MS}ms ${MAP_SLIDE_EASING}, opacity ${MAP_SLIDE_MS}ms ${MAP_SLIDE_EASING}`,
                }
              : { flex: 1, minWidth: 0 }
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            {!view.loading && <Text type="secondary">{categories.resultsLabel}</Text>}
            {/* Sits at the right edge of the list, which is exactly where the
                map's own edge is when it's open. */}
            {showMap && (
              <Button
                type="text"
                size="small"
                aria-expanded={!mapCollapsed}
                onClick={() => setMapCollapsed((collapsed) => !collapsed)}
                icon={
                  mapCollapsed ? <IconLayoutSidebarRightExpand size={18} /> : <IconLayoutSidebarRightCollapse size={18} />
                }
              >
                {translate(
                  t,
                  mapCollapsed ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SHOW_MAP : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_HIDE_MAP
                )}
              </Button>
            )}
            {/* Folded into this same "search status" row instead of
                floating alone above the list -- see filtersButton above. */}
            {isMobile && filtersButton}
          </div>

          <AnimatePresence mode="wait">
            {view.loading ? (
              <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                {/* Mobile only: the turning gears (see GearLoadingState)
                    replace the standard antd shimmer for this one screen --
                    desktop/tablet keep ResultsSkeleton as-is, that shimmer
                    already works fine there. The count row above is hidden
                    while loading, so unlike the map sheet this one does carry
                    its own line of copy. */}
                {isMobile ? (
                  <GearLoadingState message={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MOBILE_LOADING_MESSAGE)} />
                ) : (
                  <ResultsSkeleton />
                )}
              </motion.div>
            ) : view.items.length === 0 ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                <EmptyState
                  icon={<IconSearchOff size={24} />}
                  title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_NO_RESULTS)}
                  description={translate(
                    t,
                    isMachinery ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_NO_RESULTS_DESCRIPTION : TRANSLATIONS.MAP_SEARCH_NO_RESULTS_DESCRIPTION
                  )}
                />
              </motion.div>
            ) : (
              <motion.div
                key={`results-${filters.page ?? 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Row gutter={[20, 28]}>
                  {isMachinery ? items.map((space, index) => (
                    <Col xs={24} md={12} xxl={8} key={space.id}>
                      <motion.div
                        ref={(el) => {
                          cardRefs.current[space.id] = el;
                        }}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut', delay: Math.min(index, 12) * 0.04 }}
                        onMouseEnter={() => {
                          setHoveredSpaceId(space.id);
                          setHoverSource('card');
                        }}
                        onMouseLeave={() => {
                          setHoveredSpaceId(undefined);
                          setHoverSource(undefined);
                        }}
                        style={{ borderRadius: 16 }}
                      >
                        <HeavyDutyMachineryCard
                          space={space}
                          onClick={() => navigate(`/heavy-duty-machinery/${space.id}`)}
                          forceHover={hoveredSpaceId === space.id}
                        />
                      </motion.div>
                    </Col>
                  )) : view.rows.map((row) => (
                    <Col xs={24} md={12} xxl={8} key={row.pinId}>
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        onMouseEnter={() => setHoveredSpaceId(row.pinId)}
                        onMouseLeave={() => setHoveredSpaceId(undefined)}
                        style={{ borderRadius: 16 }}
                      >
                        {row.card}
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </motion.div>
            )}
          </AnimatePresence>

          <ListPagination
            current={view.page}
            total={view.totalCount}
            pageSize={view.pageSize}
            onChange={view.setPage}
            onPageSizeChange={view.setPageSize}
          />
        </div>

        {/* Two boxes: the outer one owns the layout width and animates to
            zero (or, going fullscreen, out to 100%), the inner one keeps the
            map at its full pixel width pinned to the outer's left edge. As
            the outer closes, that left edge travels right and the fixed
            right edge clips what passes it, so the map slides off to the
            side instead of being squashed flat -- and slides back in from
            the side when reopened. The left margin animates too, so a
            collapsed panel leaves no gap behind. Fullscreen skips that
            pixel-pinning (see the inner box's width below): it's growing,
            not shrinking away, so there's nothing to clip and the map's own
            ResizeObserver (SpacesMap.tsx) can resize its canvas smoothly as
            the outer box's width transitions. */}
        {showMap && (
          <div
            ref={mapPanelRef}
            onTransitionEnd={measureMapPanel}
            style={{
              width: mapCollapsed ? 0 : mapFullscreen ? '100%' : MAP_PANEL_WIDTH,
              minWidth: mapCollapsed ? 0 : MAP_PANEL_MIN_WIDTH,
              marginLeft: mapCollapsed || mapFullscreen ? 0 : MAP_PANEL_GAP,
              height: 'calc(100vh - 220px)',
              position: 'sticky',
              top: 88,
              overflow: 'hidden',
              flexShrink: 0,
              transition: `width ${MAP_SLIDE_MS}ms ${MAP_SLIDE_EASING}, min-width ${MAP_SLIDE_MS}ms ${MAP_SLIDE_EASING}, margin-left ${MAP_SLIDE_MS}ms ${MAP_SLIDE_EASING}`,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                width: mapFullscreen ? '100%' : mapPanelWidth ?? '100%',
                borderRadius: 16,
                overflow: 'hidden',
                border: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <SpacesMap
                ref={mapApiRef}
                items={view.pins}
                onBoundsChange={view.onBoundsChange}
                onSelectItem={() => {}}
                onOpenItem={view.openItem}
                fullscreen={mapFullscreen}
                onToggleFullscreen={() => setMapFullscreen((f) => !f)}
                hoveredItemId={hoveredSpaceId}
                onHoverItem={(spaceId) => {
                  setHoveredSpaceId(spaceId);
                  setHoverSource(spaceId ? 'map' : undefined);
                }}
                locationSearchSeq={view.locationSearchSeq}
                resultsLoading={view.loading}
                renderPopupCard={(pinId, closePopup) => {
                  const row = view.rows.find((r) => r.pinId === pinId);
                  if (!row) return null;
                  // The card handles its own navigation; closing the popup
                  // first keeps a stale popup from lingering over the map
                  // while the next page mounts.
                  return <div onClick={closePopup}>{row.compactCard}</div>;
                }}
              />
            </div>
          </div>
        )}
      </div>

      <FiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        facets={facets}
        totalCount={totalCount}
        onChange={updateFilters}
        onClear={clearFilters}
      />
      {categories.filtersModals}
    </div>
  );

  // Public, no-auth search/browse (see AppRoutes.tsx) -- AppLayout blanks
  // the page for anonymous visitors (its own `if (!isAuthenticated) return
  // null`), so reuse LandingLayout's header+footer chrome instead when
  // signed out (same as the homepage and the space detail page).
  if (authLoading) return null;
  if (!isAuthenticated) {
    return <LandingLayout>{content}</LandingLayout>;
  }
  return <AppLayout>{content}</AppLayout>;
};

export default HeavyDutyMachineries;
